import React from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import pencilIcon from "@iconify/icons-mdi/pencil";
import { InlineIcon } from "@iconify/react";
import {
  useMyHrChangeRequests,
  useMyHrChangeRequestsExaminersName,
  useMyHrUserRolesDataByContract,
} from "my-hr/hooks";
import { OcCustomTooltip } from "oc/components";
import { useOcCurrentUserState } from "oc/hooks";
import {
  ocSelectedTypeState,
  ocShowAddressEditState,
  ocShowEmployeeNameState,
} from "states";

interface OcCardLabelProps {
  label: string;
  translation: string;
  editable: boolean;
  def: { address?: string };
  onEdit: (address?: string) => void;
  isEditButtonDisabled: boolean;
}

export const OcCardLabel: React.FC<OcCardLabelProps> = ({
  label,
  translation,
  editable,
  def,
  onEdit,
  isEditButtonDisabled,
}) => {
  const { employeeCode, contractCode } = useOcCurrentUserState();
  const { data } = useMyHrChangeRequests(employeeCode);

  const theme = useTheme();

  const setSelectedType = useSetRecoilState(ocSelectedTypeState);
  const setShowAddressEdit = useSetRecoilState(ocShowAddressEditState);
  const setShowEmployeeNameEdit = useSetRecoilState(ocShowEmployeeNameState);

  const handleShowDataEditDialog = (value: boolean) => {
    setSelectedType(def.address);
    setShowAddressEdit(value);
  };

  const handleShowNameDataEditDialog = (value: boolean) => {
    setShowEmployeeNameEdit(value);
  };

  const isExist = data?.find(
    (item: {
      typeGroup: string;
      approvedAt: string | null;
      rejectedAt: string | null;
    }) =>
      item?.typeGroup === "name" &&
      item?.approvedAt === null &&
      item?.rejectedAt === null,
  );
  const isExistPermanentAddress = data?.find(
    (item: {
      typeGroup: string;
      approvedAt: string | null;
      rejectedAt: string | null;
    }) =>
      item?.typeGroup === "permanentAddress" &&
      item?.approvedAt === null &&
      item?.rejectedAt === null,
  );
  const isExistTemporaryAddress = data?.find(
    (item: {
      typeGroup: string;
      approvedAt: string | null;
      rejectedAt: string | null;
    }) =>
      item?.typeGroup === "temporaryAddress" &&
      item?.approvedAt === null &&
      item?.rejectedAt === null,
  );
  const isExistServiceAddress = data?.find(
    (item: {
      typeGroup: string;
      approvedAt: string | null;
      rejectedAt: string | null;
    }) =>
      item?.typeGroup === "serviceAddress" &&
      item?.approvedAt === null &&
      item?.rejectedAt === null,
  );

  const handleOnClick = () => {
    if (isEditButtonDisabled) {
      return;
    }
    onEdit(def?.address);
  };

  const { t, i18n } = useTranslation();

  const { data: examinersData } = useMyHrChangeRequestsExaminersName({
    locale: i18n.language,
    contract: contractCode,
  });

  const { data: rolesData } = useMyHrUserRolesDataByContract({
    contract: contractCode,
  });

  const isExaminer = rolesData?.some(
    (item: { product: string; role: string }) =>
      item.product === "MyHR" && item.role === "examiner",
  );

  const hasExaminer = examinersData?.length > 0;

  let isDisabled = !hasExaminer;
  if (isExaminer && !hasExaminer) {
    isDisabled = false;
  }

  if (isDisabled) {
    editable = false;
  }

  return (
    <>
      <Box
        style={{
          fontSize: "20px",
          letterSpacing: "0.15px",
          padding: def?.address ? "unset" : "0px 16px",
          color: "#5E6366",
          width: def?.address ? "100%" : "unset",
        }}
      >
        <Box
          style={{
            fontSize: "20px",
            letterSpacing: "0.15px",
            padding: def?.address ? "unset" : "10px 26px",
            color: "#5E6366",
            borderBottom: def?.address ? "" : "2px solid #C6CACC",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            className="oc-card-h6-title"
            variant="h6"
            style={{ flexGrow: "1" }}
            color="#5E6366"
          >
            <Box
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              {label}
              {editable &&
                (isExist === undefined ||
                  isExistPermanentAddress === undefined ||
                  isExistServiceAddress === undefined ||
                  isExistTemporaryAddress === undefined ? (
                  <Box
                    style={
                      isEditButtonDisabled
                        ? { cursor: "auto" }
                        : { cursor: "pointer" }
                    }
                    onClick={handleOnClick}
                  >
                    <InlineIcon
                      id={`pencilIcon-${translation}`}
                      width="18px"
                      height="18px"
                      icon={pencilIcon}
                      color={
                        isEditButtonDisabled
                          ? theme.palette.gray40.main
                          : theme.palette.gray80.main
                      }
                    />
                  </Box>
                ) : (
                  <Box
                    onClick={() =>
                      def?.address
                        ? handleShowDataEditDialog(false)
                        : handleShowNameDataEditDialog(false)
                    }
                  >
                    <InlineIcon
                      id={`pencilIcon-${translation}`}
                      width="18px"
                      height="18px"
                      icon={pencilIcon}
                      color={theme.palette.gray40.main}
                    />
                  </Box>
                ))}
              {isDisabled && (
                <OcCustomTooltip
                  title={t("OC_NOT_SET_EXAMINER_INFO")}
                  placement="top"
                >
                  <Box>
                    <InlineIcon
                      id={`pencilIcon-${translation}`}
                      width="18px"
                      height="18px"
                      icon={pencilIcon}
                      color={theme.palette.gray40.main}
                    />
                  </Box>
                </OcCustomTooltip>
              )}
            </Box>
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default OcCardLabel;
